import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Plus, Edit, Trash2, Building2, User, Mail, Globe, Save, ChevronLeft, ChevronRight, ImagePlus } from 'lucide-react';
import { getStartups, createStartup, updateStartup, deleteStartup } from '../services/apiService';

const GestionParticipadas = () => {
  const [startups, setStartups] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [startupEditando, setStartupEditando] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    fundador: '',
    arcangel: '',
    email: '',
    website: '',
    descripcion: '',
    logo_url: ''
  });

  const handleNuevaStartup = () => {
    setStartupEditando(null);
    setFormData({
      nombre: '',
      fundador: '',
      arcangel: '',
      email: '',
      website: '',
      descripcion: '',
      logo_url: ''
    });
    setMostrarFormulario(true);
  };

  const handleEditar = (startup) => {
    setStartupEditando(startup);
    setFormData({
      nombre: startup.nombre,
      fundador: startup.fundador,
      arcangel: startup.arcangel,
      email: startup.email,
      website: startup.website,
      descripcion: startup.descripcion,
      logo_url: startup.logo_url || ''
    });
    setMostrarFormulario(true);
  };

  // Cargar startups desde el backend
  useEffect(() => {
    const cargarStartups = async () => {
      try {
        const startupsData = await getStartups();
        setStartups(startupsData || []);
      } catch (error) {
        console.error('Error cargando startups:', error);
      }
    };
    cargarStartups();
  }, []);

  const handleEliminar = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta startup?')) {
      try {
        await deleteStartup(id);
        setStartups(startups.filter(s => s.id !== id));
      } catch (error) {
        console.error('Error eliminando startup:', error);
        alert('Error al eliminar la startup');
      }
    }
  };

  const handleGuardar = async () => {
    if (!formData.nombre || !formData.fundador || !formData.email) {
      alert('Por favor completa los campos obligatorios: Nombre, Fundador y Email');
      return;
    }

    try {
      if (startupEditando) {
        // Editar existente
        const updatedStartup = await updateStartup(startupEditando.id, formData);
        setStartups(startups.map(s => s.id === startupEditando.id ? updatedStartup : s));
      } else {
        // Crear nueva
        const nuevaStartup = await createStartup(formData);
        setStartups([...startups, nuevaStartup]);
      }

      setMostrarFormulario(false);
      setStartupEditando(null);
      setCurrentPage(1); // Volver a la primera página
      setFormData({
        nombre: '',
        fundador: '',
        arcangel: '',
        email: '',
        website: '',
        descripcion: '',
        logo_url: ''
      });
    } catch (error) {
      console.error('Error guardando startup:', error);
      alert('Error al guardar la startup');
    }
  };

  const handleCancelar = () => {
    setMostrarFormulario(false);
    setStartupEditando(null);
    setFormData({
      nombre: '',
      fundador: '',
      arcangel: '',
      email: '',
      website: '',
      descripcion: '',
      logo_url: ''
    });
  };

  if (mostrarFormulario) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight">
              {startupEditando ? 'Editar Startup' : 'Nueva Startup Participada'}
            </h1>
            <p className="text-muted-foreground mt-2">
              {startupEditando 
                ? 'Modifica la información de la startup'
                : 'Agrega una nueva startup en la que el club ha invertido'}
            </p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Información de la Startup</CardTitle>
            <CardDescription>
              Esta información será común a todos los reportes trimestrales
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Nombre de la Startup <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: Startup A"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Fundador <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.fundador}
                  onChange={(e) => setFormData({ ...formData, fundador: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: Juan Pérez"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Arcángel
                </label>
                <input
                  type="text"
                  value={formData.arcangel}
                  onChange={(e) => setFormData({ ...formData, arcangel: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="Ej: María García"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  Email del Fundador <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="fundador@startup.com"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background"
                  placeholder="https://startup.com"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">
                  Logo de la startup
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="url"
                    value={formData.logo_url || ''}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    className="flex-1 min-w-[200px] p-2 rounded-md border border-input bg-background"
                    placeholder="URL del logo o subir imagen debajo"
                  />
                  <label className="inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-input bg-background h-9 px-3 cursor-pointer hover:bg-accent hover:text-accent-foreground transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f && f.type.startsWith('image/')) {
                          const reader = new FileReader();
                          reader.onload = () => setFormData((prev) => ({ ...prev, logo_url: reader.result }));
                          reader.readAsDataURL(f);
                        }
                        e.target.value = '';
                      }}
                    />
                    <ImagePlus className="h-4 w-4" />
                    Subir imagen
                  </label>
                </div>
                {formData.logo_url && (
                  <div className="mt-2 flex items-center gap-2">
                    <img src={formData.logo_url} alt="Logo" className="h-14 w-14 object-contain rounded border border-input bg-muted/30" />
                  </div>
                )}
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">
                  Descripción
                </label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="w-full p-2 rounded-md border border-input bg-background min-h-[100px]"
                  placeholder="Breve descripción de la startup y su actividad principal..."
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/50">
              <Button variant="outline" onClick={handleCancelar}>
                Cancelar
              </Button>
              <Button onClick={handleGuardar}>
                <Save className="h-4 w-4 mr-2" />
                {startupEditando ? 'Guardar Cambios' : 'Crear Startup'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Gestión Participadas</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona las startups en las que el club ha invertido
          </p>
        </div>
        <Button onClick={handleNuevaStartup}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Startup
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {startups
          .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
          .map((startup) => (
          <Card key={startup.id} className="border-border/50 shadow-sm">
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                {startup.logo_url ? (
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden bg-muted/50 border border-border/50 flex items-center justify-center">
                    <img src={startup.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
                  </div>
                ) : (
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted/50 border border-border/50 flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {startup.nombre}
                  </CardTitle>
                  <CardDescription className="mt-2">
                    <div className="flex items-center gap-1 mb-1">
                      <User className="h-3 w-3" />
                      <span>{startup.fundador}</span>
                    </div>
                    {startup.arcangel && (
                      <div className="flex items-center gap-1 mb-1">
                        <User className="h-3 w-3" />
                        <span>Arcángel: {startup.arcangel}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1 mb-1">
                      <Mail className="h-3 w-3" />
                      <span className="text-xs">{startup.email}</span>
                    </div>
                    {startup.website && (
                      <div className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        <a 
                          href={startup.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline"
                        >
                          {startup.website}
                        </a>
                      </div>
                    )}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {startup.descripcion && (
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {startup.descripcion}
                </p>
              )}
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleEditar(startup)}
                  className="flex-1"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleEliminar(startup.id)}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          ))}
      </div>

      {/* Paginación */}
      {startups.length > itemsPerPage && (
        <div className="flex items-center justify-between pt-4 border-t border-border/50">
          <div className="text-sm text-muted-foreground">
            Mostrando {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, startups.length)} de {startups.length} startups
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <div className="text-sm">
              Página {currentPage} de {Math.ceil(startups.length / itemsPerPage)}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(Math.ceil(startups.length / itemsPerPage), prev + 1))}
              disabled={currentPage >= Math.ceil(startups.length / itemsPerPage)}
            >
              Siguiente
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {startups.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No hay startups registradas</p>
            <Button onClick={handleNuevaStartup}>
              <Plus className="h-4 w-4 mr-2" />
              Crear Primera Startup
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GestionParticipadas;

